// FILE: pendingSetupScriptStore.ts
// Purpose: Hand a "run this setup action once the thread is on screen" note
//          from the New thread dialog to ChatView.
// Layer: Web stores
//
// The dialog creates the worktree, but it cannot run a `terminal` action: that
// needs the thread's PTY plumbing, which only ChatView owns. So the dialog
// leaves the script id here and ChatView picks it up when it mounts the thread.
// Ephemeral on purpose — a queued action that survived a reload would fire in a
// worktree that is already set up.

import { type ThreadId } from "@taskin-code/contracts";
import { create } from "zustand";

interface PendingSetupScriptStoreState {
  scriptIdByThreadId: Record<ThreadId, string | undefined>;
  markPendingSetupScript: (threadId: ThreadId, scriptId: string) => void;
  /** Returns the pending script id and clears it, so it can only run once. */
  takePendingSetupScript: (threadId: ThreadId) => string | null;
}

export const usePendingSetupScriptStore = create<PendingSetupScriptStoreState>((set, get) => ({
  scriptIdByThreadId: {},
  markPendingSetupScript: (threadId, scriptId) => {
    if (threadId.length === 0 || scriptId.length === 0) return;
    set((state) => ({
      scriptIdByThreadId: { ...state.scriptIdByThreadId, [threadId]: scriptId },
    }));
  },
  takePendingSetupScript: (threadId) => {
    const scriptId = get().scriptIdByThreadId[threadId];
    if (scriptId === undefined) {
      return null;
    }
    set((state) => {
      const next = { ...state.scriptIdByThreadId };
      delete next[threadId];
      return { scriptIdByThreadId: next };
    });
    return scriptId;
  },
}));
